import { trackError } from '@billy/track-error'
import { useCallback } from 'react'

import { fetchContactById } from '@features/contacts/query-api'
import { InvoiceFormReceiver } from '@features/invoices/types/invoiceForm/invoiceFormReceiver'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useWatch } from '../../../../../hooks'
import { fetchContactsAndContactPersons } from '../../../../contacts/query-api'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'

// Ensures the form receiver carries both its Billy `id` and its Zervant `externalId`
// whenever the matching contact exists.
// - Missing `externalId`: the contact is fetched by id (a targeted GET, independent of the
//   capped contacts list) and the form receiver is replaced if the fetched record has one.
// - Missing `id` (e.g. a credit note whose source customer has no `externalContactId`):
//   customers are searched by name and matched on `externalId`.
// Errors are best-effort: a failing fetch is reported via trackError and the
// receiver is left untouched rather than propagating the rejection to the caller.
export const useUpdateReceiverData = () => {
  const { organization } = useUserOrganization()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { control, setValue } = invoiceEditorForm

  const receiverField = useWatch({ control, name: 'receiver' })

  const updateReceiverData = useCallback(
    async (receiver?: InvoiceFormReceiver) => {
      try {
        if (receiverField?.externalId || receiver?.externalId) {
          const receiverWithExternalId = receiver?.externalId ? receiver : receiverField
          const { externalId, id, name } = receiverWithExternalId ?? {}

          if (id || !name || !organization?.id) {
            return
          }

          const { contacts } = await fetchContactsAndContactPersons({
            isCustomer: true,
            organizationId: organization.id,
            q: name,
          })

          const contactId = contacts.find((contact) => contact.externalId === externalId)?.id

          if (contactId && receiverWithExternalId) {
            setValue('receiver', { ...receiverWithExternalId, id: contactId })
          }

          return
        }

        const contactId = receiver?.id ?? receiverField?.id

        if (!contactId) {
          return
        }

        const { contact } = await fetchContactById(contactId)

        if (contact?.externalId) {
          setValue('receiver', contact)
        }
      } catch (error) {
        trackError(error)
      }
    },
    [organization?.id, receiverField, setValue],
  )

  return { updateReceiverData }
}
